import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase/config';
import { API_ENDPOINTS, apiClient } from '../config/api';
import SmsConsentCard from '../components/settings/SmsConsentCard';
import './NotificationSettingsPage.css';

const DEADLINE_OPTIONS = [
  { value: '3_days',   label: '3 days before',   recommended: true },
  { value: '1_day',    label: '1 day before only' },
  { value: 'same_day', label: 'On the day only' },
  { value: 'off',      label: 'Off' },
];

// Debounce delay matches rialty-mobile-app NotificationSettingsScreen so both
// clients coalesce a rapid burst of toggles into a single PUT.
const SAVE_DEBOUNCE_MS = 500;

const RadioOption = ({ label, value, selected, onSelect, recommended, subLabel }) => {
  const isSelected = value === selected;
  return (
    <button
      className={`nsp-radio-row${isSelected ? ' nsp-radio-row--selected' : ''}`}
      onClick={onSelect}
    >
      <span className={`nsp-radio-outer${isSelected ? ' nsp-radio-outer--selected' : ''}`}>
        {isSelected && <span className="nsp-radio-inner" />}
      </span>
      <span className="nsp-radio-text">
        <span className={`nsp-radio-label${isSelected ? ' nsp-radio-label--selected' : ''}`}>
          {label}
          {recommended && <span className="nsp-radio-recommended"> (Recommended)</span>}
        </span>
        {subLabel && <span className="nsp-radio-sub">{subLabel}</span>}
      </span>
      {isSelected && (
        <svg className="nsp-radio-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
    </button>
  );
};

const Toggle = ({ checked, onChange }) => (
  <label className="nsp-toggle">
    <input type="checkbox" checked={checked} onChange={onChange} />
    <span className="nsp-toggle-track">
      <span className="nsp-toggle-thumb" />
    </span>
  </label>
);

const getCurrentUserId = () => {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.id || null;
  } catch {
    return null;
  }
};

const NotificationSettingsPage = () => {
  const navigate = useNavigate();

  const [appNotifications,   setAppNotifications]   = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(false);
  const [deadlineReminder,   setDeadlineReminder]   = useState('3_days');
  const [loaded, setLoaded] = useState(false);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error

  const saveTimerRef = useRef(null);

  // Load from GET /api/v1/users/{userId}/profile.preferences — the same
  // storage that the mobile client (rialty-mobile-app NotificationSettingsScreen)
  // reads and writes. The prior implementation hit a non-existent
  // /api/v1/users/me/notification-settings, so the page rendered defaults
  // and silently ignored every change.
  useEffect(() => {
    const fetchSettings = async () => {
      const userId = getCurrentUserId();
      if (!userId) { setLoaded(true); return; }
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) { setLoaded(true); return; }
        const token = await currentUser.getIdToken(true);
        const res = await apiClient.get(API_ENDPOINTS.userProfile(userId), {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const prefs = res.data?.preferences || {};
        if (typeof prefs.emailNotificationsEnabled === 'boolean') {
          setEmailNotifications(prefs.emailNotificationsEnabled);
        }
        if (typeof prefs.appNotifications === 'boolean') {
          setAppNotifications(prefs.appNotifications);
        }
        if (typeof prefs.deadlineReminder === 'string' && prefs.deadlineReminder) {
          setDeadlineReminder(prefs.deadlineReminder);
        }
      } catch (err) {
        // 404 on profile-not-yet-created is the common case — keep defaults.
        if (err?.response?.status && err.response.status !== 404) {
          console.error('Failed to load notification preferences:', err);
        }
      } finally {
        setLoaded(true);
      }
    };
    fetchSettings();
  }, []);

  // Debounced merge-save. Body matches mobile: only send the changed subset
  // when we can, and the backend merges into UserProfile.preferences (see
  // ProfileService.updateProfile).
  const savePreferences = useCallback(async (patch) => {
    const userId = getCurrentUserId();
    if (!userId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        setSaveState('saving');
        const currentUser = auth.currentUser;
        if (!currentUser) throw new Error('Not authenticated');
        const token = await currentUser.getIdToken(true);
        await apiClient.put(
          API_ENDPOINTS.userProfile(userId),
          { preferences: patch },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        setSaveState('saved');
        setTimeout(() => setSaveState(s => (s === 'saved' ? 'idle' : s)), 1500);
      } catch (err) {
        console.error('Failed to save notification preferences:', err);
        setSaveState('error');
      }
    }, SAVE_DEBOUNCE_MS);
  }, []);

  // Flush pending save on unmount so a fast back-navigation doesn't lose the
  // last toggle.
  useEffect(() => () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
  }, []);

  const handleAppToggle = () => {
    const next = !appNotifications;
    setAppNotifications(next);
    savePreferences({ appNotifications: next });
  };

  const handleEmailToggle = () => {
    const next = !emailNotifications;
    setEmailNotifications(next);
    savePreferences({ emailNotificationsEnabled: next });
  };

  const handleDeadlineChange = (value) => {
    setDeadlineReminder(value);
    savePreferences({ deadlineReminder: value });
  };

  return (
    <div className="nsp-page">
      <div className="nsp-main-content">
        <div className="nsp-content-box">
          <div className="nsp-content">

        {/* ── Page Header ── */}
        <div className="nsp-page-header">
          <button className="nsp-back-btn" onClick={() => navigate(-1)}>
            <span>←</span>
            <span>Back</span>
          </button>
          <h1 className="nsp-page-title">Notification Settings</h1>
          <p className="nsp-page-subtitle">Manage how and when you receive notifications</p>
          {loaded && saveState !== 'idle' && (
            <p
              className="nsp-save-status"
              role="status"
              data-state={saveState}
              style={{
                fontSize: 12,
                marginTop: 6,
                color: saveState === 'error' ? '#c0392b'
                     : saveState === 'saved' ? '#2c8f4f'
                     : '#666',
              }}
            >
              {saveState === 'saving' && 'Saving…'}
              {saveState === 'saved'  && 'Saved'}
              {saveState === 'error'  && 'Could not save. Please try again.'}
            </p>
          )}
        </div>

        {/* ── Toggle Card — App & Email ── */}
        <div className="nsp-card">
          <div className="nsp-toggle-row">
            <div className="nsp-toggle-icon-wrap">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              </svg>
            </div>
            <div className="nsp-toggle-info">
              <span className="nsp-toggle-label">App Notifications</span>
              <span className="nsp-toggle-desc">Push alerts on your device</span>
            </div>
            <Toggle checked={appNotifications} onChange={handleAppToggle} />
          </div>

          <div className="nsp-divider" />

          <div className="nsp-toggle-row">
            <div className="nsp-toggle-icon-wrap">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
            </div>
            <div className="nsp-toggle-info">
              <span className="nsp-toggle-label">Email Notifications</span>
              <span className="nsp-toggle-desc">Receive updates via email</span>
            </div>
            <Toggle checked={emailNotifications} onChange={handleEmailToggle} />
          </div>
        </div>

        {/* ── Text messages — consent is recorded by the server, not saved like the toggles above ── */}
        <SmsConsentCard />

        {/* ── Deadline Reminders — only when email on ── */}
        {emailNotifications && (
          <div className="nsp-card">
            <div className="nsp-section-header">
              <div className="nsp-section-title-row">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
                <span className="nsp-section-title">Deadline Reminders</span>
              </div>
              <p className="nsp-section-subtitle">Choose when you'd like to receive reminders for upcoming deadlines:</p>
            </div>
            <div className="nsp-divider" />
            {DEADLINE_OPTIONS.map((opt, i) => (
              <React.Fragment key={opt.value}>
                {i > 0 && <div className="nsp-radio-separator" />}
                <RadioOption
                  value={opt.value}
                  label={opt.label}
                  recommended={opt.recommended}
                  selected={deadlineReminder}
                  onSelect={() => handleDeadlineChange(opt.value)}
                />
              </React.Fragment>
            ))}
            <div className="nsp-card-bottom-spacer" />
          </div>
        )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettingsPage;
